import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { clearAuth, getUser } from '../utils/auth';
import { useGlobalPresence } from '../components/GlobalPresenceProvider';
import AssignmentsList from '../components/AssignmentsList';

function StudentView() {
  const navigate = useNavigate();
  const user = getUser();
  const { connectToClass } = useGlobalPresence();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Connect to class when selected
  useEffect(() => {
    if (selectedClass?.id) {
      connectToClass(selectedClass.id);
    }
  }, [selectedClass?.id, connectToClass]);

  // Auto-connect to last active class or first enrolled class when page loads
  useEffect(() => {
    if (classes.length > 0) {
      // Check if we have a saved active class
      const savedClassId = localStorage.getItem('activeClassId');
      if (savedClassId) {
        // Verify student is still enrolled in this class
        const savedClass = classes.find(c => c.id === parseInt(savedClassId));
        if (savedClass) {
          connectToClass(savedClass.id);
          return;
        }
      }
      // Otherwise connect to first enrolled class
      connectToClass(classes[0].id);
    }
  }, [classes, connectToClass]);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const data = await apiClient.get('/classes');
      setClasses(data.classes);
    } catch (err) {
      setError('Failed to load classes');
    }
  };

  const handleJoinClass = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiClient.post('/classes/join', { join_code: joinCode });
      setSuccess(response.message);
      setJoinCode('');
      await loadClasses();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterClass = (classObj) => {
    navigate(`/whiteboard/${classObj.id}`);
  };

  const handleSelectClass = (classObj) => {
    setSelectedClass(classObj);
  };

  const handleSelectAssignment = (assignment) => {
    // Navigate to whiteboard with assignment ID
    navigate(`/whiteboard/${selectedClass.id}?assignment=${assignment.id}`);
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/welcome');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>
          Minu klassid
        </h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            {user?.display_name}
          </span>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Logi välja
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
        {/* Join Class Form */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
            Liitu klassiga
          </h2>
          
          <form onSubmit={handleJoinClass}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="Sisesta liitumise kood"
                required
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  textTransform: 'uppercase'
                }}
              />
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  background: '#2563ff',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Liitumine...' : 'Liitu'}
              </button>
            </div>
          </form>

          {error && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              borderRadius: '8px',
              background: '#fef2f2',
              color: '#b91c1c',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              borderRadius: '8px',
              background: '#ecfdf3',
              color: '#166534',
              fontSize: '14px'
            }}>
              {success}
            </div>
          )}
        </div>

        {/* Classes List or Selected Class View */}
        {!selectedClass ? (
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
              Minu klassid
            </h2>

            {classes.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '48px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '16px'
              }}>
                Sa pole veel ühtegi klassiga liitunud
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {classes.map((classObj) => (
                  <div
                    key={classObj.id}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
                        {classObj.name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        Liitus: {new Date(classObj.joined_at).toLocaleDateString('et-EE')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleSelectClass(classObj)}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '8px',
                          background: '#8b5cf6',
                          color: 'white',
                          border: 'none',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        Ülesanded
                      </button>
                      <button
                        onClick={() => handleEnterClass(classObj)}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '8px',
                          background: '#2563ff',
                          color: 'white',
                          border: 'none',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        Vaba tahvel →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* Back button and class name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <button
                onClick={() => setSelectedClass(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ← Tagasi
              </button>
              <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>
                {selectedClass.name}
              </h2>
            </div>

            {/* Assignments */}
            <AssignmentsList
              classId={selectedClass.id}
              isTeacher={false}
              onSelectAssignment={handleSelectAssignment}
            />

            {/* Free whiteboard option */}
            <div style={{
              marginTop: '24px',
              padding: '20px',
              background: '#f9fafb',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px' }}>
                Soovid lahendada oma ülesannet?
              </p>
              <button
                onClick={() => handleEnterClass(selectedClass)}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  background: '#2563ff',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Ava vaba tahvel →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentView;